import { useCallback, useEffect, useState } from 'react';
import { MAX_CART_ITEM_QUANTITY_PRODUCT_DETAIL, MIN_CART_ITEM_QUANTITY } from '../constants/cart.constants';
import { AddToCartErrorTypeEnum } from '../enums/add-to-cart-error-type.enum';
import {
    type CartItemInterface,
    type ConfigurableCartItem,
    type ConfigurableVariant,
    type Maybe,
} from '../types/front.graphql';
import { type CartItemLocalInterface, CartItemTypeEnum } from '../models/cart.model';
import {
    getCartItemVariation as getCartItemVariationUtil,
    updateCartItemQuantityAndPrices,
} from '../helpers/cart-item.helpers';
import isEmpty from 'lodash/isEmpty';
import { useAddToCart, useCreateEmptyCart, useRemoveItemFromCart, useUpdateCartItem } from './cart-mutation.hook';
import type { ApolloError } from '@apollo/client';
import { useCheckoutCartActions } from './checkout-cart/checkout-cart-actions.hook';
import { type CartItemReferenceAction, type CartItemReference, type CartItemReferenceLogic } from '../models';
import { useCartItemReference } from './cart-item-reference/use-cart-item-reference.hook';
import {
    forceCartItemReferenceSync,
    withInternalUpdate,
    markQuantityPending,
    markUserAction,
} from '../stores/cart-item-reference.store';
import { useRouter } from 'next/navigation';
import { type ProductModel } from '@kidslabs/module-catalog';
import { isNumber } from '@kidslabs/module-core';
import { useSelector } from '@legendapp/state/react';
import { cartStoreAPI } from '../stores/cart-store.public-api';
import { cartItemReferenceStoreAPI } from '../stores/cart-item-reference-store.public-api';
import { type Observable } from '@legendapp/state';

// Cart item type for actions - flexible to handle both CartItemInterface and CartItemLocalInterface
// Using 'any' here due to complex GraphQL Maybe type incompatibilities between modules
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
type CartActionItem = any; // CartItemInterface | CartItemLocalInterface with nullable handling

export type CartActionProps = {
    item: CartActionItem;
    cartItemReference?: CartItemReference;
    value: number | string;
    cartItemReferenceLogics?: CartItemReferenceLogic[];
    parentProduct?: ProductModel;
};

type AvailableAddToCartActionProps = CartActionProps & { isGift: boolean };

interface CartActionsHook {
    handleIncreaseQuantity: (props: CartActionProps) => void;
    handleBuyNow: (props: CartActionProps) => void;
    handleDecreaseQuantity: (props: CartActionProps) => void;
    handleUpdateQuantity: (props: CartActionProps) => void;
    handleChangeQuantity: (props: CartActionProps) => void;
    validateAddToCartAction: (props: CartActionProps) => AddToCartErrorTypeEnum | undefined;
    getCartItemVariation: (props: CartActionProps) => ConfigurableVariant | undefined;
    getAvailableAddToCartAction: (props: AvailableAddToCartActionProps) => boolean;
    handleRemoveItemInCart: (props: CartActionProps) => void;
    handleRemoveItemsInCart: (props: CartActionProps[]) => void;
    handleRemoveItemInCheckoutCart: (props: CartActionProps) => void;
    handleRemoveItemsInCheckoutCart: (props: CartActionProps[]) => void;
    handleIncreaseQuantityInCheckoutCart: (props: CartActionProps[]) => void;
    loading: boolean;
    errorMessage?: string;
    isItemInCart: (item: CartActionItem) => boolean;
    getItemFromCart: (item: CartActionItem) => CartActionItem | undefined; // eslint-disable-line @typescript-eslint/no-redundant-type-constituents
    isItemInCheckoutCart: (item: CartActionItem) => boolean;
    getItemFromCheckoutCart: (item: CartActionItem) => Maybe<CartItemInterface> | undefined;
    handleChangeQuantityInCheckoutCart: (props: CartActionProps) => void;
    handleCheckoutRemoveItemsInCart: (props: CartActionProps[]) => void;
    setQuantityStepper: (value: number) => void;
    getValidEnrichedCartItemReferences: ({
        showOnlySelected,
        cartItemReferences,
    }: {
        showOnlySelected: boolean;
        cartItemReferences: CartItemReference[];
    }) => CartItemReference[];
    getAllLogicActions: ({
        cartItemReferences,
    }: {
        cartItemReferences: CartItemReference[];
    }) => CartItemReferenceAction[];
    removeCartItemReference: (cartItemReferenceId: string) => void;
    setCartItemReferenceSelected: (cartItemReferenceId: string, isSelected: boolean) => void;
    handleSelectAllCartItemReference: (checked: boolean) => void;
    isAllCartItemReferenceSelected: boolean;
    /** Find first item by product ID without gift logics */
    itemByProductIdWithoutGifts$: Observable<(productId: number) => CartItemLocalInterface | undefined>;
}

export const useCartActionsV3 = (): CartActionsHook => {
    const router = useRouter();
    const {
        itemByProductIdWithoutGifts$,
        addCartItemReference,
        updateCartItemReference,
        removeCartItemReference,
        initCartItemReference,
        isPromotionReference,
        getReferenceById,
        getCartItemReferencesByProductId,
        setCartItemReferenceSelected,
        setCartItemReferences,
        unselectedAllCartItemReferenceSelected,
        checkAvailableToBuyCartItemReference,
        getAvailableToBuyCartItemReferences,
        references$,
    } = useCartItemReference();
    const {
        handleIncreaseQuantity: handleCheckoutIncreaseQuantity,
        handleChangeQuantity: handleCheckoutChangeQuantity,
        handleRemoveItemInCart: handleCheckoutRemoveItemInCart,
        handleRemoveItemsInCart: handleCheckoutRemoveItemsInCart,
        initCart: initCheckoutCart,
        loading: checkoutCartLoading,
        errorMessage: checkoutCartErrorMessage,
        isItemInCart: isItemInCheckoutCart,
        getItemInCart: getItemFromCheckoutCart,
    } = useCheckoutCartActions();

    const [actionLoading, setActionLoading] = useState(false);
    const [actionErrorMessage, setActionErrorMessage] = useState<string | undefined>(undefined);
    const [isCreatingEmptyCart, setIsCreatingEmptyCart] = useState(false);
    const { createEmptyCart } = useCreateEmptyCart();
    const {
        addToCartServerSide,
        addSimpleToCartLoading: isAddingSimpleProduct,
        addConfigurableToCartLoading: isAddingConfigurableProduct,
    } = useAddToCart();
    const { updateCartItemServerSide, loading: isUpdatingCartItem } = useUpdateCartItem();
    const { removeItemFromCartServerSide, loading: isRemovingItemFromCart } = useRemoveItemFromCart();

    const handleError = (error: ApolloError | Error) => {
        if ('graphQLErrors' in error && error?.graphQLErrors?.length) {
            const errorMessage = error.graphQLErrors[0].message;
            setActionErrorMessage(errorMessage);
        } else if ('message' in error) {
            const errorMessage = error.message;
            if (errorMessage.includes('GraphQL error:')) {
                setActionErrorMessage(errorMessage.replace('GraphQL error: ', ''));
            } else {
                setActionErrorMessage(errorMessage);
            }
        } else {
            setActionErrorMessage('An unknown error occurred.');
        }
    };

    const initializeCartId = async (): Promise<string> => {
        setIsCreatingEmptyCart(true);
        let cartId = cartStoreAPI.state.getCartId();
        if (!cartId || cartId === '') {
            try {
                setActionLoading(true);
                const emptyCartIdResponse = await createEmptyCart();
                cartId = emptyCartIdResponse?.data?.createEmptyCart ?? '';
                if (isEmpty(cartId)) {
                    throw new Error('Failed to create an empty cart.');
                }
                cartStoreAPI.actions.setCartId(cartId);
                initCheckoutCart(cartId);
            } catch (error) {
                handleError(error as ApolloError | Error);
                setIsCreatingEmptyCart(false);
                setActionLoading(false);
                return '';
            }
        }

        setIsCreatingEmptyCart(false);
        setActionLoading(false);
        return cartId || 'no-cart-id';
    };

    const getCartItemVariation = ({ item }: CartActionProps): ConfigurableVariant | undefined => {
        let variation;
        const typeName = (item as { __typename?: string }).__typename;
        // Use type assertion since __typename may not be explicitly typed in CartItemInterface
        if (typeName === CartItemTypeEnum.CONFIGURABLE || typeName === CartItemTypeEnum.CONFIGURABLE_LOCAL) {
            variation = getCartItemVariationUtil(item as ConfigurableCartItem);
        }
        return variation;
    };

    const validateAddToCartAction = (props: CartActionProps): AddToCartErrorTypeEnum | undefined => {
        const { item } = props;
        if (actionLoading) {
            return AddToCartErrorTypeEnum.CART_IS_INITIALIZING;
        }

        const variation = getCartItemVariation(props);

        if (
            '__typename' in (item?.product ?? {}) &&
            (item?.product as ProductModel)?.__typename === 'ConfigurableProduct' &&
            !variation
        ) {
            return AddToCartErrorTypeEnum.MISSING_SELECTED_VARIANT;
        }

        return undefined;
    };

    const getAvailableAddToCartAction = ({ item, isGift }: AvailableAddToCartActionProps) => {
        return item.id.startsWith('new-') || isGift;
    };

    const resetErrorMessage = () => {
        setActionErrorMessage(undefined);
    };

    /**
     * Helper: Extract the correct product ID from a cart item.
     * For configurable products with parentProduct, use configured_variant.id.
     * Otherwise, use product.id.
     */
    const getProductId = (item: CartActionItem, parentProduct?: ProductModel): number => {
        const typeName = (item as { __typename?: string }).__typename;
        const isConfigurableItem =
            typeName === CartItemTypeEnum.CONFIGURABLE || typeName === CartItemTypeEnum.CONFIGURABLE_LOCAL;
        if (isConfigurableItem || parentProduct) {
            const configurableItem = item as ConfigurableCartItem;
            return configurableItem.configured_variant?.id || item?.product?.id || 0;
        }
        return item?.product?.id || 0;
    };

    /** Helper: check if any reference has gift/promotion */
    const hasGiftReference = (refs: CartItemReference[]) => refs.some(ref => isPromotionReference(ref));

    /** Helper: create new CartItemReference from item and qty, add to store */
    const createAndAddCartItemReference = (
        item: CartActionItem,
        qty: number,
        logics: CartItemReferenceLogic[] = [],
        isSelected: boolean = true,
        parentProduct?: ProductModel
    ): CartItemReference => {
        // Derive variant SKU for configurable products
        let sku: string | undefined;
        const typeName = (item as { __typename?: string }).__typename;
        const isConfigurable =
            typeName === CartItemTypeEnum.CONFIGURABLE || typeName === CartItemTypeEnum.CONFIGURABLE_LOCAL;

        if (parentProduct && isConfigurable) {
            const configurableItem = item as ConfigurableCartItem;
            sku = configurableItem.configured_variant?.sku ?? item?.product?.sku;
        } else {
            sku = item?.product?.sku;
        }

        const newCartItemReference = initCartItemReference({
            productId: getProductId(item, parentProduct),
            qty,
            logics,
            cartItemId: item.localId ?? item.id,
            isSelected,
            createdAt: new Date().toISOString(),
            parentProduct,
            addToStore: false,
        });
        newCartItemReference.cartItem = item as unknown as CartItemLocalInterface;
        newCartItemReference.sku = sku;
        addCartItemReference(newCartItemReference);
        return newCartItemReference;
    };

    /**
     * Helper: Check if an item exists in the cart by looking up CartItemReferences by productId.
     */
    const isItemInCart = (item: CartActionItem): boolean => {
        const productId = getProductId(item);
        const refs = getCartItemReferencesByProductId(productId);
        return refs.length > 0;
    };

    /**
     * Helper: Get cartItem from CartItemReference store for a given item.
     * Searches references by productId and returns the cartItem from the first matching reference.
     */
    const getItemFromCart = (item: CartActionItem): CartActionItem => {
        const productId = getProductId(item);
        const refs = getCartItemReferencesByProductId(productId);

        for (const ref of refs) {
            if (ref.cartItem) {
                return ref.cartItem;
            }
        }

        return undefined;
    };

    const findBaseCartItemReference = ({
        productId,
        cartItemReference,
        cartItemReferenceLogics,
    }: {
        productId: number;
        cartItemReference?: CartItemReference;
        cartItemReferenceLogics?: CartItemReferenceLogic[];
    }): CartItemReference | undefined => {
        if (cartItemReference) {
            return cartItemReference;
        }

        const referenceIdFromLogics = cartItemReferenceLogics?.[0]?.cartItemReferenceId;
        if (referenceIdFromLogics) {
            return getReferenceById(referenceIdFromLogics);
        }

        const refs = getCartItemReferencesByProductId(productId);
        return refs.find(ref => !ref.logics || ref.logics.length === 0);
    };

    const resolveResultReference = ({
        item,
        quantity,
        parentProduct,
        cartItemReferenceLogics,
        existingCartItemReference,
        hasNewLogics,
    }: {
        item: CartActionItem;
        quantity: number;
        parentProduct?: ProductModel;
        cartItemReferenceLogics?: CartItemReferenceLogic[];
        existingCartItemReference?: CartItemReference;
        hasNewLogics: boolean;
    }): CartItemReference | undefined => {
        if (hasNewLogics) {
            return createAndAddCartItemReference(item, quantity, cartItemReferenceLogics, true, parentProduct);
        }

        if (!existingCartItemReference) {
            return createAndAddCartItemReference(item, quantity, [], true, parentProduct);
        }

        if (hasGiftReference([existingCartItemReference])) {
            setActionErrorMessage('Cannot increase quantity because gift has been received.');
            return undefined;
        }

        return existingCartItemReference;
    };

    const buildUpdatedCartItem = ({
        item,
        itemToIncrease,
        resultReference,
        quantity,
        isReplace,
        isNewRef,
    }: {
        item: CartActionItem;
        itemToIncrease: CartActionItem;
        resultReference?: CartItemReference;
        quantity: number;
        isReplace?: boolean;
        isNewRef: boolean;
    }): CartItemLocalInterface => {
        const sourceItem = (!isNewRef && resultReference?.cartItem ? resultReference.cartItem : itemToIncrease) || item;

        const existingCartItem = resultReference?.cartItem;

        if (existingCartItem && !isNewRef) {
            const newQuantity = isReplace ? quantity : (resultReference?.qty || 0) + quantity;
            return updateCartItemQuantityAndPrices(existingCartItem, newQuantity);
        }

        const newCartItem = updateCartItemQuantityAndPrices(
            sourceItem as unknown as CartItemLocalInterface,
            quantity
        ) as CartItemLocalInterface;

        newCartItem.cartItemReferenceId = resultReference?.cartItemReferenceId ?? '';
        return newCartItem;
    };

    const persistUpdatedReference = ({
        resultReference,
        item,
        quantity,
        isReplace,
        isNewRef,
        cartItemToUpdate,
        parentProduct,
    }: {
        resultReference?: CartItemReference;
        item: CartActionItem;
        quantity: number;
        isReplace?: boolean;
        isNewRef: boolean;
        cartItemToUpdate: CartItemLocalInterface;
        parentProduct?: ProductModel;
    }): CartItemReference => {
        if (resultReference) {
            const nextReference = { ...resultReference };

            if (nextReference.isSelected && !nextReference.cartId) {
                nextReference.cartId = cartStoreAPI.state.getCartId();
            }

            if (!nextReference.cartItemId) {
                nextReference.cartItemId = item.id;
            }

            let newReferenceQty = quantity;

            if (!isNewRef && !isReplace) {
                newReferenceQty = nextReference.qty + quantity;
            }

            const updatedReference: CartItemReference = {
                ...nextReference,
                qty: newReferenceQty,
                cartItem: cartItemToUpdate,
            };

            updateCartItemReference(updatedReference);

            // Mark pending to prevent server overwrites during debounce window
            markQuantityPending(updatedReference.cartItemReferenceId, newReferenceQty);

            return updatedReference;
        }

        const refs = getCartItemReferencesByProductId(item?.product?.id || 0);
        const ref = refs.find(r => !isPromotionReference(r));

        if (ref) {
            const updatedRef: CartItemReference = {
                ...ref,
                qty: isReplace ? quantity : ref.qty + quantity,
                cartItem: cartItemToUpdate,
            };
            updateCartItemReference(updatedRef);
            return updatedRef;
        }

        return createAndAddCartItemReference(item, quantity, [], true, parentProduct);
    };

    const handleIncreaseQuantityInner = (props: CartActionProps & { isReplace?: boolean }) => {
        resetErrorMessage();

        const {
            item: itemToIncrease,
            value,
            cartItemReferenceLogics,
            parentProduct,
            cartItemReference,
            isReplace,
        } = props;

        const productId = getProductId(itemToIncrease, parentProduct);
        const quantity = isNumber(value) ? Number(value) : 1;
        const hasNewLogics = !!cartItemReferenceLogics?.length;

        const existingCartItemReference = findBaseCartItemReference({
            productId,
            cartItemReference,
            cartItemReferenceLogics,
        });

        const resultReference = resolveResultReference({
            item: itemToIncrease,
            quantity,
            parentProduct,
            cartItemReferenceLogics,
            existingCartItemReference,
            hasNewLogics,
        });

        if (!resultReference) {
            return;
        }

        const isNewRef = hasNewLogics || !existingCartItemReference;
        const cartItemToUpdate = buildUpdatedCartItem({
            item: itemToIncrease,
            itemToIncrease,
            resultReference,
            quantity,
            isReplace,
            isNewRef,
        });

        return persistUpdatedReference({
            resultReference,
            item: itemToIncrease,
            quantity,
            isReplace,
            isNewRef,
            cartItemToUpdate,
            parentProduct,
        });
    };

    const handleIncreaseQuantityOuter = (props: CartActionProps) => {
        resetErrorMessage();
        // TODO-4.7: Mark user action to invalidate stale server data
        markUserAction();
        initializeCartId()
            .then(() => {
                handleIncreaseQuantityInner(props);
                setActionLoading(false);
            })
            .catch(error => {
                console.log('Error initializing cart for increase quantity action:', error);
            });
    };

    const handleBuyNow = (props: CartActionProps) => {
        resetErrorMessage();

        initializeCartId()
            .then(async () => {
                // Suppress auto-sync during buy-now mutations to prevent race conditions.
                // Without this, debounce timers fire during the buy-now flow and sync
                // stale selection state where previously-selected items are still checked.
                const cartItemReference = await withInternalUpdate(async () => {
                    const ref = handleIncreaseQuantityInner(props);

                    if (ref) {
                        unselectedAllCartItemReferenceSelected();
                        setCartItemReferenceSelected(ref.cartItemReferenceId, true);
                    }

                    return ref;
                });

                if (cartItemReference) {
                    // Force sync to server (auto-sync re-enabled, bypass debounce)
                    await forceCartItemReferenceSync();

                    router.push('/payment/checkout');

                    setActionLoading(false);
                }
            })
            .catch(error => {
                console.log('Error initializing cart for Buy Now action:', error);
            });
    };

    const handleDecreaseQuantity = (props: CartActionProps) => {
        resetErrorMessage();
        const { item, value, cartItemReference, parentProduct } = props;

        const productId = getProductId(item, parentProduct);

        let existingCartItemReference = cartItemReference;
        if (!existingCartItemReference) {
            const refs = getCartItemReferencesByProductId(productId);
            existingCartItemReference = refs.find(r => !r.logics || r.logics.length === 0);
        }

        if (!existingCartItemReference) {
            setActionErrorMessage('Cannot find cart reference to decrease quantity.');
            return;
        }

        if (hasGiftReference([existingCartItemReference])) {
            setActionErrorMessage('Cannot decrease quantity because gift has been received.');
            return;
        }

        const quantity = isNumber(value) ? Number(value) : 1;
        const newReferenceQty = existingCartItemReference.qty - quantity;
        const existingCartItem = existingCartItemReference.cartItem;

        if (!existingCartItem) {
            setActionErrorMessage('Cannot decrease quantity. Please use Delete button to remove product from cart.');
            return;
        }

        const newCartItemQuantity = (existingCartItem.quantity || 0) - quantity;

        // TODO-4.7: Mark user action to invalidate stale server data
        markUserAction();

        if (newReferenceQty <= 0 || newCartItemQuantity <= 0) {
            removeCartItemReference(existingCartItemReference.cartItemReferenceId);
        } else {
            const cartItemToUpdate = updateCartItemQuantityAndPrices(
                existingCartItem,
                newCartItemQuantity
            ) as CartItemLocalInterface;

            const updatedReference: CartItemReference = {
                ...existingCartItemReference,
                qty: newReferenceQty,
                cartItem: cartItemToUpdate,
            };
            updateCartItemReference(updatedReference);

            // Mark pending to prevent server overwrites during debounce window
            markQuantityPending(existingCartItemReference.cartItemReferenceId, newReferenceQty);
        }
    };

    /**
     * Change quantity in CartItemReference store. Uses handleIncreaseQuantityInner with isReplace flag.
     */
    const handleChangeQuantityInCartItemReference = (props: CartActionProps) => {
        setActionErrorMessage(undefined);
        const { item, value, parentProduct, cartItemReferenceLogics, cartItemReference } = props;

        const productId = getProductId(item, parentProduct);
        const refs = getCartItemReferencesByProductId(productId);
        if (hasGiftReference(refs)) {
            setActionErrorMessage('Cannot change quantity because gift has been received.');
            return;
        }

        let updateQuantity = isNumber(value) ? Number(value) : MIN_CART_ITEM_QUANTITY;
        if (updateQuantity < MIN_CART_ITEM_QUANTITY) {
            return;
        }
        if (updateQuantity > MAX_CART_ITEM_QUANTITY_PRODUCT_DETAIL) {
            updateQuantity = MAX_CART_ITEM_QUANTITY_PRODUCT_DETAIL;
        }

        if (updateQuantity <= 0) {
            const existingRef = cartItemReference || refs.find(r => !r.logics || r.logics.length === 0);
            if (existingRef) {
                removeCartItemReference(existingRef.cartItemReferenceId);
            }
            return;
        }

        handleIncreaseQuantityInner({
            item,
            value: updateQuantity,
            cartItemReferenceLogics,
            parentProduct,
            cartItemReference,
            isReplace: true,
        });
    };

    const handleUpdateQuantity = (props: CartActionProps) => {
        resetErrorMessage();
        handleChangeQuantityInCartItemReference(props);
    };

    const handleChangeQuantity = (props: CartActionProps) => {
        resetErrorMessage();
        const { value: valueInput, item, parentProduct, cartItemReference } = props;
        let updateQuantity = isNumber(valueInput) ? Number(valueInput) : MIN_CART_ITEM_QUANTITY;
        if (updateQuantity < MIN_CART_ITEM_QUANTITY) {
            return;
        }
        if (updateQuantity > MAX_CART_ITEM_QUANTITY_PRODUCT_DETAIL) {
            updateQuantity = MAX_CART_ITEM_QUANTITY_PRODUCT_DETAIL;
        }

        if (!isItemInCart(item)) {
            setActionErrorMessage(
                'Item is not in the cart or has not been added yet. Please add the item to the cart first.'
            );
            return;
        }

        // ════════════════════════════════════════════════════════════════════
        // OPTIMISTIC UPDATE: Update local CartItemReference store immediately.
        // The store's debounced sync handler (800ms debounce + 3s maxWait)
        // will take care of syncing to the server. This eliminates the
        // double-debounce race condition that caused quantity jumping.
        // ════════════════════════════════════════════════════════════════════

        // TODO-4.7: Mark user action to invalidate stale server data
        markUserAction();

        // Find the CartItemReference to get its ID for pending tracking
        const productId = getProductId(item, parentProduct);
        const existingRef =
            cartItemReference ?? getCartItemReferencesByProductId(productId).find(r => !isPromotionReference(r));

        // Update the CartItemReference store locally (optimistic, immediate UI update)
        handleChangeQuantityInCartItemReference({
            ...props,
            value: updateQuantity,
        });

        // Mark this item as having a pending quantity change.
        // This prevents server responses (from previous sync or fetchCartItem)
        // from overwriting the user's latest local quantity during the debounce window.
        if (existingRef) {
            markQuantityPending(existingRef.cartItemReferenceId, updateQuantity);
        }

        // Also update checkout cart if applicable
        handleCheckoutChangeQuantity({ ...props, value: updateQuantity });
    };

    const handleRemoveItemInCart = async (props: CartActionProps) => {
        resetErrorMessage();
        const { item, parentProduct } = props;
        const cartId = cartStoreAPI.state.getCartId();

        // TODO-4.7 + TODO-6.5: Mark user action to invalidate stale server data
        // and notify coupon/gift system that cart composition changed
        markUserAction();

        // Remove all references for this product from CartItemReference store
        const productId = getProductId(item, parentProduct);
        const refs = getCartItemReferencesByProductId(productId);
        refs.forEach(ref => removeCartItemReference(ref.cartItemReferenceId));

        if (cartId) {
            setActionLoading(true);
            removeItemFromCartServerSide(cartId, +item.id)
                .then(() => {
                    handleCheckoutRemoveItemInCart(props);
                })
                .catch((innerError: ApolloError | Error) => {
                    handleError(innerError);
                    // Re-add on error (rollback)
                    handleIncreaseQuantityInner(props);
                })
                .finally(() => {
                    setActionLoading(false);
                });
            return;
        }
    };

    const handleRemoveItemsInCartInner = async (props: CartActionProps[]) => {
        resetErrorMessage();
        setActionLoading(true);
        try {
            props.forEach(({ item, cartItemReference }) => {
                if (cartItemReference) {
                    removeCartItemReference(cartItemReference.cartItemReferenceId);
                } else {
                    const productId = getProductId(item);
                    const refs = getCartItemReferencesByProductId(productId);
                    refs.forEach(ref => removeCartItemReference(ref.cartItemReferenceId));
                }
            });
        } catch (error) {
            console.error('Unexpected error while removing items from cart:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemoveItemInCheckoutCart = async (props: CartActionProps) => {
        resetErrorMessage();
        const { item } = props;
        const cartId = cartStoreAPI.state.getCartId();
        if (!cartId) return;
        setActionLoading(true);
        try {
            await removeItemFromCartServerSide(cartId, +item.id);
            handleCheckoutRemoveItemInCart(props);
        } catch (innerError) {
            handleError(innerError as ApolloError | Error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemoveItemsInCheckoutCart = async (props: CartActionProps[]) => {
        resetErrorMessage();
        const cartId = cartStoreAPI.state.getCartId();
        if (!cartId) return;
        setActionLoading(true);
        try {
            const results = await Promise.allSettled(
                props.map(({ item }) => removeItemFromCartServerSide(cartId, +item.id))
            );
            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    handleCheckoutRemoveItemInCart(props[index]);
                } else {
                    const { item } = props[index];
                    console.error(`Failed to remove item with id ${item.id}:`, result.reason);
                }
            });
        } catch (error) {
            console.error('Unexpected error while removing items from checkout cart:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const increaseCheckoutCartItemQuantity = async (cartId: string, cartActionProps: CartActionProps) => {
        const { item, value } = cartActionProps;
        let currentQuantity = 0;
        const cartItem = getItemFromCart(item);
        if (cartItem && isNumber(cartItem.quantity)) {
            currentQuantity = Number(cartItem.quantity);
        }
        const increaseBy = isNumber(value) ? Number(value) : 1;
        if (cartItem?.id.startsWith('new-')) {
            const variation = getCartItemVariation(cartActionProps);
            await addToCartServerSide(cartId, item?.product, currentQuantity + increaseBy, variation);
        } else {
            await updateCartItemServerSide(cartId, +item.id, currentQuantity + increaseBy);
        }

        handleCheckoutIncreaseQuantity({ item, value });
    };

    const handleIncreaseQuantityInCheckoutCart = async (cartActionsProps: CartActionProps[]) => {
        resetErrorMessage();
        const cartId = cartStoreAPI.state.getCartId();
        if (!cartId) {
            return;
        }
        setActionLoading(true);
        try {
            await Promise.all(
                cartActionsProps.map(cartActionProps => increaseCheckoutCartItemQuantity(cartId, cartActionProps))
            );
        } catch (error) {
            handleError(error as ApolloError | Error);
        } finally {
            setActionLoading(false);
        }
    };

    const getValidEnrichedCartItemReferences = ({
        showOnlySelected,
        cartItemReferences,
    }: {
        showOnlySelected: boolean;
        cartItemReferences: CartItemReference[];
    }): CartItemReference[] => {
        const filtered = cartItemReferences.filter(ref => !!ref.cartItem && (!showOnlySelected || ref.isSelected));

        return filtered.sort((a, b) => {
            // 1. Selected items first
            const aSelected = a.isSelected ? 1 : 0;
            const bSelected = b.isSelected ? 1 : 0;
            if (aSelected !== bSelected) return bSelected - aSelected;

            // 2. Newest first (createdAt DESC)
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime;
        });
    };

    const getAllLogicActions = ({ cartItemReferences }: { cartItemReferences: CartItemReference[] }) => {
        return cartItemReferences.flatMap(items => items?.logics?.flatMap(logic => logic?.actions));
    };

    useEffect(() => {
        setActionLoading(
            isAddingSimpleProduct ||
                isAddingConfigurableProduct ||
                isUpdatingCartItem ||
                isRemovingItemFromCart ||
                isCreatingEmptyCart ||
                checkoutCartLoading
        );
    }, [
        checkoutCartLoading,
        isAddingConfigurableProduct,
        isAddingSimpleProduct,
        isCreatingEmptyCart,
        isRemovingItemFromCart,
        isUpdatingCartItem,
    ]);

    useEffect(() => {
        if (checkoutCartErrorMessage) {
            setActionErrorMessage(checkoutCartErrorMessage);
        } else {
            setActionErrorMessage(undefined);
        }
    }, [checkoutCartErrorMessage]);

    const removeCartItemReferenceInner = (cartItemReferenceId: string) => {
        removeCartItemReference(cartItemReferenceId);
    };

    const setCartItemReferenceSelectedInner = (cartItemReferenceId: string, isSelected: boolean) => {
        let promise: Promise<string> = Promise.resolve('');
        if (isSelected) {
            promise = initializeCartId();
        }

        promise
            .then(() => {
                setCartItemReferenceSelected(cartItemReferenceId, isSelected);
            })
            .catch(error => {
                console.error('Error initializing cart for setCartItemReferenceSelected action:', error);
            });
    };

    const isAllCartItemReferenceSelected = useSelector(() => {
        const references = references$().get() ?? [];
        if (references.length === 0) {
            return false;
        }
        const availableToBuyCartItemReferences = getAvailableToBuyCartItemReferences();
        return (
            availableToBuyCartItemReferences.length > 0 && availableToBuyCartItemReferences.every(ref => ref.isSelected)
        );
    });

    const handleCheckAll = useCallback(
        (nextChecked: boolean) => {
            const refs = references$().get() ?? [];
            const updatedCartItemReferences: CartItemReference[] = refs.map(cartItemReference => {
                const availableToBuy = checkAvailableToBuyCartItemReference(cartItemReference);

                if (!availableToBuy) {
                    return cartItemReference;
                }

                return {
                    ...cartItemReference,
                    isSelected: nextChecked,
                };
            });
            setCartItemReferences(updatedCartItemReferences);
        },
        [checkAvailableToBuyCartItemReference, references$, setCartItemReferences]
    );

    const handleSelectAllCartItemReference = useCallback(
        (checked: boolean) => {
            handleCheckAll(checked);
        },
        [handleCheckAll]
    );

    return {
        handleIncreaseQuantity: handleIncreaseQuantityOuter,
        handleDecreaseQuantity,
        handleChangeQuantity,
        handleUpdateQuantity,
        handleBuyNow,
        validateAddToCartAction,
        getCartItemVariation,
        getAvailableAddToCartAction,
        handleRemoveItemInCart,
        handleRemoveItemsInCart: handleRemoveItemsInCartInner,
        handleRemoveItemInCheckoutCart,
        handleRemoveItemsInCheckoutCart,
        handleIncreaseQuantityInCheckoutCart,
        loading: actionLoading,
        errorMessage: actionErrorMessage,
        isItemInCart: isItemInCart,
        getItemFromCart: getItemFromCart,
        isItemInCheckoutCart,
        getItemFromCheckoutCart: getItemFromCheckoutCart,
        handleChangeQuantityInCheckoutCart: handleCheckoutChangeQuantity,
        handleCheckoutRemoveItemsInCart: handleCheckoutRemoveItemsInCart,
        setQuantityStepper: cartItemReferenceStoreAPI.actions.setQuantityStepper,
        getValidEnrichedCartItemReferences,
        removeCartItemReference: removeCartItemReferenceInner,
        setCartItemReferenceSelected: setCartItemReferenceSelectedInner,
        handleSelectAllCartItemReference,
        isAllCartItemReferenceSelected,
        getAllLogicActions,
        itemByProductIdWithoutGifts$,
    };
};
